import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspaceResolver } from '@/hooks/useWorkspaceResolver';
import { Lead } from '@/hooks/useLeads';
import { useAuth } from '@/lib/auth';
import { createDealNotifications } from '@/lib/dealNotifications';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { Loader2, DollarSign } from 'lucide-react';

interface CreateDealModalProps {
  lead: Lead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STAGES = [
  { value: 'new', label: 'New' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'negotiation', label: 'Negotiation' },
];

export function CreateDealModal({ lead, open, onOpenChange }: CreateDealModalProps) {
  const { workspaceId } = useWorkspaceResolver();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    title: `Deal with ${lead.name}`,
    value: '',
    currency: 'USD',
    stage: 'new',
    expected_close_date: '',
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('No workspace available');

      // Use DealsService instead of direct supabase call
      const { DealsService } = await import('@/data/deals.service');
      return DealsService.createDeal({
        workspace_id: workspaceId,
        lead_id: lead.id,
        title: formData.title || `Deal with ${lead.name}`,
        value: formData.value ? parseFloat(formData.value) : null,
        currency: formData.currency,
        stage: formData.stage as any,
        expected_close_date: formData.expected_close_date || null,
      });
    },
    onSuccess: async (createdDeal) => {
      // Invalidate with correct workspaceId
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['revenue-analytics', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-stats', workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ['leads', workspaceId] }), // Also refresh leads
        queryClient.invalidateQueries({ queryKey: ['notifications'] }), // Refresh notifications
      ]);
      
      // Force refetch to ensure deals appear immediately
      await queryClient.refetchQueries({ queryKey: ['deals', workspaceId] });
      
      // Always create at least one immediate notification (deal_in_play).
      // If expected_close_date is set, helper will also schedule reminders.
      if (workspaceId && user?.id) {
        try {
          await createDealNotifications({
            dealId: createdDeal.id,
            leadId: lead.id,
            workspaceId,
            userId: user.id,
            expectedCloseDate: formData.expected_close_date || null,
            dealTitle: formData.title || `Deal with ${lead.name}`,
            dealValue: formData.value ? parseFloat(formData.value) : null,
          });
        } catch (error) {
          // Non-blocking: log but don't fail the deal creation
          console.warn('[CreateDealModal] Failed to create notifications', error);
        }
      }
      
      toast({ title: 'Deal created', description: 'The deal has been added to your pipeline.' });
      onOpenChange(false);
      // Reset form
      setFormData({
        title: `Deal with ${lead.name}`,
        value: '',
        currency: 'USD',
        stage: 'new',
        expected_close_date: '',
      });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            Create Deal
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Lead</Label>
            <div className="p-3 bg-muted rounded-md">
              <p className="font-medium">{lead.name}</p>
              {lead.company && (
                <p className="text-sm text-muted-foreground">{lead.company}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Deal Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Deal title"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="value">Value</Label>
              <Input
                id="value"
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="10000"
                min="0"
                step="0.01"
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={formData.currency}
                onValueChange={(v) => setFormData({ ...formData, currency: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="BRL">BRL (R$)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Stage</Label>
            <Select
              value={formData.stage}
              onValueChange={(v) => setFormData({ ...formData, stage: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value}>
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="expected_close_date">Expected Close Date</Label>
            <Input
              id="expected_close_date"
              type="date"
              value={formData.expected_close_date}
              onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contrast" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Deal
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
